import { useMemo } from 'react';
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { withDevtools, StoreName } from '../config';
import { EntityLabel } from 'components/common/SearchInput/types';
import { useProjectId } from 'store/project';
import { useSearchUpdate } from 'hooks/search/useSearchUpdate';
import { useInitialSearchEffect } from 'hooks/search/useInitialSearch';
import { useCreateStore } from 'hooks/createStore';

type SearchState = {
  searches: Record<string, string>;
  setSearchPhrase: (entity: EntityLabel, phrase: string) => void;
  getSearchPhrase: (entity: EntityLabel) => string;
};

export const useSearchStore = create<SearchState>()(
  withDevtools(
    subscribeWithSelector((set, get) => ({
      searches: {},
      setSearchPhrase: (entity, phrase) =>
        set((state) => ({
          searches: { ...state.searches, [entity]: phrase },
        })),
      getSearchPhrase: (entity) =>
        (get() as unknown as SearchState).searches[entity] || '',
    })),
    StoreName.Search
  )
);

export const useEntitySearch = (
  entity: EntityLabel
): {
  searchPhrase: string;
  setSearchPhrase: (p: string | ((prev: string) => string)) => void;
  searchPhraseStore: ReturnType<typeof useCreateStore<string>>;
} => {
  const projectId = useProjectId();
  const phrase = useSearchStore((s) => s.searches[entity] || '');
  const setSearchPhrase = useSearchStore((s) => s.setSearchPhrase);

  const setPhrase = (p: string | ((prev: string) => string)): void => {
    const newValue = typeof p === 'function' ? p(phrase) : p;
    setSearchPhrase(entity, newValue);
  };

  useSearchUpdate(entity, setPhrase);
  useInitialSearchEffect(projectId, entity, setPhrase);

  const searchPhraseStore = useMemo(
    () => ({
      get: () => useSearchStore.getState().searches[entity] || '',
      set: (value: string) => setSearchPhrase(entity, value),
      subscribe: (callback: () => void) => {
        return useSearchStore.subscribe((state, prev) => {
          const curr = state.searches[entity] || '';
          const prevVal = prev?.searches?.[entity] || '';
          if (curr !== prevVal) callback();
        });
      },
    }),
    [setSearchPhrase, entity]
  );

  return {
    searchPhrase: phrase,
    setSearchPhrase: setPhrase,
    searchPhraseStore,
  };
};
